import { createStyles, Group } from '@mantine/core';
import type { Filter } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { Text } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { useQuery } from '@tanstack/react-query';
import { fetchAllPages } from '../../api/factories';
import { resourceCatalogQueryKeyFactory } from '../../api/hooks/resourceCatalog/constants';
import type { ISecodaEntity } from '../../api/types';
import { toArray } from '../../api/utils/iterator';
import { RelatedResourcePill } from '../InlineRelatedResources/RelatedResourcePill';

const useStyles = createStyles((theme) => ({
	wrapper: {
		flex: '1 !important',
		minHeight: space[7],
		maxWidth: '100%',
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing.xs,
		overflow: 'hidden',
		alignItems: 'center',
	},
}));

export interface InlineGlossaryNestedTermsProps {
	glossaryTermId: string;
}

const PAGE_SIZE = 100;

export function InlineGlossaryNestedTerms({
	glossaryTermId,
}: InlineGlossaryNestedTermsProps) {
	const { classes } = useStyles();

	// Fetch every page so the list is never truncated to the first page.
	const { data: nestedTerms } = useQuery({
		queryKey: resourceCatalogQueryKeyFactory.byArgs(
			'nested-glossary-terms',
			glossaryTermId
		),
		queryFn: async () =>
			(await toArray(
				fetchAllPages(resourceCatalogQueryKeyFactory, {
					filter: {
						operator: 'and',
						operands: [
							{
								field: 'entity_type',
								operator: 'exact',
								value: EntityType.glossary,
							},
							{
								field: 'parent_id',
								operator: 'exact',
								value: glossaryTermId,
							},
						],
					} as Filter,
					sort: {
						field: 'title',
						order: 'asc',
					},
					page_size: PAGE_SIZE,
				})
			)) as ISecodaEntity[],
	});

	if (!nestedTerms?.length) {
		return null;
	}

	return (
		<Group spacing="xs" noWrap align="flex-start">
			<Text w={145} size="sm" py="4xs">
				Glossary terms
			</Text>
			<Group align="flex-start" className={classes.wrapper}>
				{nestedTerms.map((term) => (
					<RelatedResourcePill key={term.id} entity={term} />
				))}
			</Group>
		</Group>
	);
}
